/* mp-cc-quick-view-image-fix.js — fallbacks/additions for ConvertCart's Quick
   View modal:
   1. Its product image ships with no `src`, and its thumbnail strip
      (.cc-thumb-sec) with no thumbnail elements at all, on mobile (both
      confirmed live via DevTools — CC's own script never sets a main-image
      src, and its thumbnail slider track renders as a genuinely empty,
      0-width div rather than one populated with images that just lack a
      src). Fixed using data Shopify already exposes via
      /products/{handle}.js — reading the handle from the modal's own "view
      full details" link (a.cc-prod-link) that sits right next to the main
      image. Also builds a minimal working thumbnail strip (click-to-swap
      the main image) from that same data.
   2. The cloned quantity <input type="number"> never gets a native up/down
      spinner on mobile — that's not fixable via CSS, mobile browsers simply
      don't render one for type="number" at all (desktop-only UA behavior).
      Adds real +/- buttons around it instead (mp-cc-quick-view-fix.css
      hides them above 749px, since desktop already shows the native
      spinner once restored there — see product-css.liquid).

   Never reimplements the modal itself — only fills in what CC's own script
   fails to produce, or adds controls mobile browsers can't render natively.

   CC reuses a single modal DOM node across different products (repopulating
   it per click rather than creating a new one each time), so "already
   fixed" is tracked by product handle on the modal itself for the image fix,
   and by a dataset flag on the quantity input for the stepper — otherwise
   the second product opened would never get fixed once the first one had
   been. */
(function () {
  function normalizeSrc(src) {
    if (src && src.indexOf('//') === 0) return 'https:' + src;
    return src;
  }

  function buildThumbnails(thumbSec, mainImg, product) {
    var track = thumbSec.querySelector('.cc-thumb-slider') || thumbSec;
    track.style.overflow = 'visible';
    track.innerHTML = '';

    var wrap = document.createElement('div');
    wrap.className = 'mp-qv-thumbs';
    product.images.forEach(function (src, i) {
      var url = normalizeSrc(src);
      var thumb = document.createElement('img');
      thumb.className = 'mp-qv-thumb' + (i === 0 ? ' mp-qv-thumb--active' : '');
      thumb.src = url;
      thumb.alt = product.title || '';
      thumb.loading = 'lazy';
      thumb.addEventListener('click', function () {
        mainImg.setAttribute('src', url);
        wrap.querySelectorAll('.mp-qv-thumb').forEach(function (t) {
          t.classList.remove('mp-qv-thumb--active');
        });
        thumb.classList.add('mp-qv-thumb--active');
      });
      wrap.appendChild(thumb);
    });
    track.appendChild(wrap);
  }

  function addQtyStepper(modal) {
    var qtyInput = modal.querySelector('.product-form__item--quantity input[type="number"]');
    if (!qtyInput || qtyInput.dataset.mpQvStepper) return;
    qtyInput.dataset.mpQvStepper = 'true';

    function step(delta) {
      var min = parseInt(qtyInput.min, 10) || 1;
      var val = Math.max(min, (parseInt(qtyInput.value, 10) || min) + delta);
      qtyInput.value = val;
      qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // The real PDP quantity field now ships its own -/+ stepper (see
    // sections/product-template.liquid's .mp-pdp-qty-stepper), which CC's
    // clone script drags into this modal along with the rest of
    // .product-single__meta — but the cloned buttons come across inert,
    // since inline <script> tags and event listeners don't survive a DOM
    // clone. Re-wire those instead of building a second, redundant stepper
    // UI on top of them.
    var clonedWrap = qtyInput.closest('.mp-pdp-qty-stepper');
    if (clonedWrap) {
      var clonedMinus = clonedWrap.querySelector('.mp-pdp-qty-minus');
      var clonedPlus = clonedWrap.querySelector('.mp-pdp-qty-plus');
      if (clonedMinus) clonedMinus.addEventListener('click', function () { step(-1); });
      if (clonedPlus) clonedPlus.addEventListener('click', function () { step(1); });
      return;
    }

    // Fallback for when the real form has no stepper to clone (shouldn't
    // normally happen now, kept for resilience): mimics the native desktop
    // spinner's look (two tiny stacked arrows on the input's right edge).
    var wrap = document.createElement('div');
    wrap.className = 'mp-qv-qty-wrap';
    qtyInput.parentNode.insertBefore(wrap, qtyInput);
    wrap.appendChild(qtyInput);

    var arrows = document.createElement('div');
    arrows.className = 'mp-qv-qty-arrows';

    var plus = document.createElement('button');
    plus.type = 'button';
    plus.className = 'mp-qv-qty-btn mp-qv-qty-plus';
    plus.setAttribute('aria-label', 'Increase quantity');

    var minus = document.createElement('button');
    minus.type = 'button';
    minus.className = 'mp-qv-qty-btn mp-qv-qty-minus';
    minus.setAttribute('aria-label', 'Decrease quantity');

    arrows.appendChild(plus);
    arrows.appendChild(minus);
    wrap.appendChild(arrows);

    plus.addEventListener('click', function () { step(1); });
    minus.addEventListener('click', function () { step(-1); });
  }

  function fixModal(modal) {
    var link = modal.querySelector('.cc-product-image a.cc-prod-link, .cc-product-image a[href*="/products/"]');
    if (!link) return;
    var handle = link.getAttribute('href');
    if (!handle || modal.dataset.mpQvFixing === handle) return;

    var mainImg = modal.querySelector('.cc-product-image img');
    var thumbSec = modal.querySelector('.cc-thumb-sec');
    var needsMain = mainImg && !mainImg.getAttribute('src');
    var needsThumbs = thumbSec
      && thumbSec.querySelectorAll('img').length === 0
      && !thumbSec.querySelector('.mp-qv-thumbs');
    if (!needsMain && !needsThumbs) return;

    modal.dataset.mpQvFixing = handle;
    fetch(handle + '.js')
      .then(function (res) { return res.json(); })
      .then(function (product) {
        if (needsMain && mainImg) {
          var mainSrc = normalizeSrc(product.featured_image || (product.images && product.images[0]));
          if (mainSrc) {
            mainImg.setAttribute('src', mainSrc);
            mainImg.setAttribute('alt', product.title || '');
          }
        }
        if (needsThumbs && mainImg && product.images && product.images.length > 1) {
          buildThumbnails(thumbSec, mainImg, product);
        }
      })
      .catch(function () {})
      .then(function () {
        if (modal.dataset.mpQvFixing === handle) delete modal.dataset.mpQvFixing;
      });
  }

  function scan() {
    document.querySelectorAll('[class*="cc-quick-view-"]').forEach(function (modal) {
      fixModal(modal);
      addQtyStepper(modal);
    });
  }

  // The MutationObserver below watches the whole page body (needed since CC's
  // quick-view modal can appear/toggle anywhere), so it fires on completely
  // unrelated DOM churn too — lazy-loaded images, carousels, CC's own
  // extensive DOM patching. scan()'s `[class*="cc-quick-view-"]` query is a
  // substring attribute selector (slow, no class-name index) run against the
  // whole document, so calling it on every single mutation batch was
  // measured costing ~800ms of CPU. Debouncing collapses a burst of many
  // mutations into one scan, with no functional difference — a modal opening
  // still gets fixed within a fraction of a second.
  var scanTimer = null;
  function debouncedScan() {
    if (scanTimer) return;
    scanTimer = setTimeout(function () {
      scanTimer = null;
      scan();
    }, 100);
  }

  new MutationObserver(debouncedScan).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  scan();
})();
